import React from "react";
import { useNavigate, Link } from "react-router-dom";
import supabaseClient from "../services/supabase";
import { IS_DEV } from "../utils/misc";
import { useAuth } from "../hooks/useAuth";
import "../styles/Globals.css";

/* ------------------------------------------------------------------ */
/*  Small Google SVG icon                                             */
/* ------------------------------------------------------------------ */
const GoogleIcon = () => (
  <svg
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    className="w-4 h-4 mr-2"
  >
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Main Page                                                         */
/* ------------------------------------------------------------------ */
const Home: React.FC = () => {
  const navigate = useNavigate();
  const { sessionStatus } = useAuth();

  const isSignedIn = sessionStatus !== "signed_out";
  const hasProfile = sessionStatus === "signed_in_with_profile";

  /* ----------------------------------------------------------------
   *  Google OAuth sign‑in
   * ---------------------------------------------------------------- */
  const handleSignIn = async () => {
    if (IS_DEV) console.log("[Home] Starting Google sign‑in");
    const { error } = await supabaseClient.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    });
    if (error) console.error("[Home] Google sign‑in error:", error);
  };

  /* ----------------------------------------------------------------
   *  UI (renders only if user is not signed in yet)
   * ---------------------------------------------------------------- */
  return (
    <div className="flex min-h-screen flex-col bg-[#f8f8f8] text-[#333333]">
      {/* Header */}
      <header className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-between">
          <div className="text-xl font-light tracking-wider">crackd</div>
          <nav className="hidden space-x-6 text-sm font-light md:flex">
            <Link to="/about" className="transition-opacity hover:opacity-70">
              about
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <main className="container mx-auto flex flex-1 flex-col items-center justify-center px-4 py-12 text-center">
        <div className="relative mb-12 w-24 h-[21px]">
          {/* animated background lines */}
          <div className="absolute top-0 left-16 w-8 h-[1px] bg-[#333333]/20"></div>
          <div className="absolute top-[10px] left-8 w-8 h-[1px] bg-[#333333]/20"></div>
          <div className="absolute top-[20px] left-0 w-8 h-[1px] bg-[#333333]/20"></div>
          <div className="absolute top-[1px] left-16 w-[1px] h-[9px] bg-[#333333]/20"></div>
          <div className="absolute top-[11px] left-8 w-[1px] h-[9px] bg-[#333333]/20"></div>
          <div
            className="absolute h-[3px] w-[3px] bg-[#333333]/40 animated-cube"
            style={{ top: "16px", left: "0px" }}
          />
        </div>

        <h1 className="mb-6 max-w-md text-3xl font-light tracking-wide md:text-4xl">
          technical students, ranked by peers.
        </h1>
        <p className="mb-12 max-w-md text-sm font-light text-[#333333]/70">
          see where your technical skills stand amongst your peers.
        </p>

        {/* Sign‑in button (only show if the user is not signed in) */}
        {!isSignedIn && (
          <div className="flex flex-col items-center">
            <button
              onClick={handleSignIn}
              className="inline-flex items-center border border-[#333333]/20 rounded-md px-4 py-2 text-sm font-light transition-all hover:border-[#333333]/60"
            >
              <GoogleIcon />
              Sign in with Google
            </button>
            <p className="mt-2 text-2xs font-light text-[#333333]/70">
              Only .edu email addresses allowed
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer
        className="container mx-auto px-4 py-6 text-xs font-light text-[#333333]/50"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="flex flex-col justify-between space-y-4 md:flex-row md:space-y-0">
          <div>© 2025 crackd. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
